import { Button, Skeleton } from '@mantine/core';
import styled from '@emotion/styled';
import { AppLoader } from '../../../../components/common/loader';
import { PreviewTable } from '../../draft/table/previewTable';
import { CustomSelect } from '../../styles';
import { moduleRoles } from '../../../../constants';
import { useState } from 'react';
import { showAlert } from '../../../../utils/alerts';
import { axios } from '../../../../utils/axios';
import { isRequestBased, isTransferPricing } from './constants';

const StyledButton = styled(Button)`
  max-height: 46px;
`;

const SpocTable = ({ data, users, loading, setData }) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selections, setSelections] = useState(data);

  const submit = async () => {
    try {
      setSubmitLoading(true);
      const { role, country, plant, type_of_request, primary_spoc, secondary_spoc } = selections;

      await axios.post('/costing-request/add-primary-secondary-spoc', {
        role: role,
        country: country,
        plant: plant,
        type_of_request: type_of_request,
        primary_spoc: primary_spoc,
        secondary_spoc: secondary_spoc,
      });

      setData({});
      showAlert({ message: 'SPOC has been updated successfully' }, 'success');
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const submitBackupSpoc = async () => {
    try {
      setSubmitLoading(true);
      const { role, costing_request_id, backup_spoc } = selections;

      await axios.post('/costing-request/add-backup-spoc', {
        role: role,
        costing_request_id: costing_request_id,
        backup_spoc,
      });
      setData({});
      showAlert({ message: 'Backup SPOC has been updated successfully' }, 'success');
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const onChange = (name, value) => {
    setSelections((o) => {
      return {
        ...o,
        [name]: value,
      };
    });
  };

  const checkIfRequestBased = () => {
    return isRequestBased(selections.base);
  };

  const isSubmitDisabled =
    (!checkIfRequestBased() && !selections.primary_spoc) ||
    (!checkIfRequestBased() && !selections.secondary_spoc) ||
    (checkIfRequestBased() && !selections.backup_spoc);

  const columns = [
    checkIfRequestBased() && {
      id: 'cr_id',
      header: 'Costing Request ID',
      Cell: ({}) => selections.costing_request_id,
    },
    !checkIfRequestBased() && {
      accessorKey: 'type_of_request',
      header: 'Type',
    },
    {
      accessorKey: 'role',
      header: 'Role',
      Cell: ({
        row: {
          original: { role },
        },
      }) => {
        const roles = moduleRoles['Costing Request'];
        const alias = roles.find((item) => item.value === role);

        return alias.label;
      },
    },
    !checkIfRequestBased() &&
      !isTransferPricing(selections.type_of_request) && {
        accessorKey: 'plant',
        header: 'Plant',
      },
    !checkIfRequestBased() && {
      accessorKey: 'country',
      header: 'Country',
    },
    {
      id: 'primary_spoc',
      header: 'Primary SPOC',
      Cell: ({
        row: {
          original: { primary_spoc, secondary_spoc, backup_spoc },
        },
      }) => (
        <Skeleton visible={loading}>
          <CustomSelect
            placeholder='Select Primary SPOC'
            value={primary_spoc}
            onChange={(value) => onChange('primary_spoc', value)}
            data={users.filter((user) => ![secondary_spoc, backup_spoc].includes(user.value))}
            searchable
            disabled={checkIfRequestBased()}
            withinPortal
          />
        </Skeleton>
      ),
    },
    {
      id: 'secondary_spoc',
      header: 'Secondary SPOC',
      Cell: ({
        row: {
          original: { primary_spoc, secondary_spoc, backup_spoc },
        },
      }) => (
        <Skeleton visible={loading}>
          <CustomSelect
            value={secondary_spoc}
            placeholder='Select Secondary SPOC'
            onChange={(value) => onChange('secondary_spoc', value)}
            data={users.filter((user) => ![primary_spoc, backup_spoc].includes(user.value))}
            searchable
            disabled={checkIfRequestBased()}
            withinPortal
          />
        </Skeleton>
      ),
    },

    checkIfRequestBased() && {
      id: 'backup_spoc',
      header: 'Backup SPOC',
      Cell: ({
        row: {
          original: { primary_spoc, secondary_spoc, backup_spoc },
        },
      }) => (
        <Skeleton visible={loading}>
          <CustomSelect
            value={backup_spoc}
            placeholder='Select Backup SPOC'
            onChange={(value) => onChange('backup_spoc', value)}
            data={users.filter((user) => ![primary_spoc, secondary_spoc].includes(user.value))}
            searchable
            withinPortal
          />
        </Skeleton>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      Cell: () => (
        <StyledButton
          disabled={isSubmitDisabled || loading || submitLoading}
          color='dark'
          fullWidth
          size='lg'
          onClick={checkIfRequestBased() ? submitBackupSpoc : submit}
        >
          {submitLoading ? <AppLoader size='sm' variant='dark' /> : 'Submit'}
        </StyledButton>
      ),
    },
  ];

  return (
    <PreviewTable
      columns={columns.filter((c) => c)}
      data={[selections]}
      dependencies={[users.length, loading]}
      enableZoom={true}
      //   zoom={1}
    />
  );
};

export default SpocTable;
